import {
  Badge,
  Body1,
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Dropdown,
  Field,
  Input,
  Option,
  Text,
  Textarea,
  Tooltip,
  mergeClasses,
} from '@fluentui/react-components';
import {
  Copy16Filled,
  Copy16Regular,
  Delete16Filled,
  Delete16Regular,
  Edit16Filled,
  Edit16Regular,
  bundleIcon,
} from '@fluentui/react-icons';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  DATASET_ROLE_TOOLTIP_ANNOTATOR,
  DATASET_ROLE_TOOLTIP_CONTRIBUTOR,
} from '../../../../constants/index.js';
import type {
  AnnotationSchema,
  AnnotationSet,
} from '../../../../services/dataHubService.interface.js';
import type { DataHubService } from '../../../../services/dataHubService.js';
import { withCorrelationId } from '../../../../services/usageContext.js';
import { useScenarioFactory } from '../../../../telemetry/hooks/useScenarioFactory/useScenarioFactory.js';
import { classifyScenarioError } from '../../../../telemetry/scenarioError.js';
import { ScenarioName } from '../../../../telemetry/scenarios.js';
import { AnnotationDataTable } from '../AnnotationData/AnnotationDataTable.js';
import { preflightAnnotationPublish } from '../AnnotationEditor/index.js';
import { useStyles } from '../DatasetAnnotationsTab.style.js';

const CopyIcon = bundleIcon(Copy16Filled, Copy16Regular) as React.FC;
const EditIcon = bundleIcon(Edit16Filled, Edit16Regular) as React.FC;
const DeleteIcon = bundleIcon(Delete16Filled, Delete16Regular) as React.FC;

const COPY_FEEDBACK_MS = 1500;

function formatDateTime(dateStr?: string): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function truncateId(id: string): string {
  return id.length > 16 ? `${id.slice(0, 16)}…` : id;
}

function resolveSchemaName(
  schemaId: string,
  schemas: AnnotationSchema[],
): string {
  return schemas.find((s) => s.id === schemaId)?.name ?? schemaId;
}

// ── Confirm dialog (reusable) ───────────────────────────────────────────

interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  confirmLabel: string;
  isPending: boolean;
  confirmDisabled?: boolean;
  destructive?: boolean;
  onConfirm: () => void;
}

function ConfirmActionDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  isPending,
  confirmDisabled = false,
  destructive,
  onConfirm,
}: ConfirmDialogProps) {
  const styles = useStyles();
  return (
    <Dialog open={open} onOpenChange={(_, d) => onOpenChange(d.open)}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{title}</DialogTitle>
          <DialogContent>
            <Text>{description}</Text>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => onOpenChange(false)} disabled={isPending}>
              Cancel
            </Button>
            <Button
              appearance="primary"
              onClick={onConfirm}
              disabled={isPending || confirmDisabled}
              className={destructive ? styles.destructiveButton : undefined}
            >
              {confirmLabel}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

// ── Main expanded row ───────────────────────────────────────────────────

export interface AnnotationSetExpandedRowProps {
  annotationSet: AnnotationSet;
  dataSetId: string;
  dataSetVersion: number;
  schemas: AnnotationSchema[];
  service: DataHubService;
  canEdit: boolean;
  canPublish: boolean;
  onRefresh: () => void;
}

export function AnnotationSetExpandedRow({
  annotationSet,
  dataSetId,
  dataSetVersion,
  schemas,
  service,
  canEdit,
  canPublish,
  onRefresh,
}: AnnotationSetExpandedRowProps) {
  const styles = useStyles();
  const scenarioFactory = useScenarioFactory();
  const isDraft = annotationSet.status === 'Draft';
  const schemaName = resolveSchemaName(annotationSet.schemaId, schemas);

  // ── Local state ──────────────────────────────────────────────────────
  const [copiedSetId, setCopiedSetId] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Edit form
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState(annotationSet.name);
  const [editDesc, setEditDesc] = useState(annotationSet.description ?? '');
  const [isSaving, setIsSaving] = useState(false);

  // Confirm dialogs
  const [publishOpen, setPublishOpen] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [discardDraftOpen, setDiscardDraftOpen] = useState(false);
  const [isDiscarding, setIsDiscarding] = useState(false);
  const [deleteAllOpen, setDeleteAllOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [hardDeleteOpen, setHardDeleteOpen] = useState(false);
  const [isHardDeleting, setIsHardDeleting] = useState(false);

  // Version selector
  const [selectedVersion, setSelectedVersion] = useState<string>('active');

  const versionOptions = useMemo(() => {
    const items: { value: string; label: string }[] = [
      {
        value: 'active',
        label: `Active — v${annotationSet.version} (${annotationSet.status})`,
      },
    ];
    for (let v = annotationSet.version - 1; v >= 1; v--) {
      items.push({ value: String(v), label: `v${v}` });
    }
    return items;
  }, [annotationSet.version, annotationSet.status]);

  const currentVersionLabel = useMemo(
    () =>
      versionOptions.find((o) => o.value === selectedVersion)?.label ??
      versionOptions[0]!.label,
    [versionOptions, selectedVersion],
  );

  const isViewingActive = selectedVersion === 'active';

  // ── Copy set ID ──────────────────────────────────────────────────────
  const handleCopySetId = useCallback(() => {
    void navigator.clipboard
      .writeText(annotationSet.annotationSetId)
      .then(() => {
        setCopiedSetId(true);
        setTimeout(() => setCopiedSetId(false), COPY_FEEDBACK_MS);
      });
  }, [annotationSet.annotationSetId]);

  // ── Edit handlers ────────────────────────────────────────────────────
  const handleSave = useCallback(async () => {
    setError(null);
    setIsSaving(true);
    const scenario = scenarioFactory.newScenario(
      ScenarioName.DatalakeUpdateAnnotationSet,
    );
    try {
      await service.updateAnnotationSet(
        dataSetId,
        annotationSet.annotationSetId,
        {
          name: editName.trim(),
          description: editDesc.trim() || undefined,
        },
        { extras: withCorrelationId(scenario.queryId) },
      );
      scenario.complete('success');
      setEditing(false);
      onRefresh();
    } catch (err) {
      const classification = classifyScenarioError(err);
      scenario.complete(classification.status, classification.data);
      setError(
        err instanceof Error ? err.message : 'Failed to update annotation set',
      );
    } finally {
      setIsSaving(false);
    }
  }, [
    service,
    dataSetId,
    annotationSet.annotationSetId,
    editName,
    editDesc,
    onRefresh,
    scenarioFactory,
  ]);

  // ── Action handlers ──────────────────────────────────────────────────
  const handlePublish = useCallback(async () => {
    if (!canPublish) {
      setError(DATASET_ROLE_TOOLTIP_CONTRIBUTOR);
      return;
    }
    setError(null);
    setIsPublishing(true);
    const scenario = scenarioFactory.newScenario(
      ScenarioName.DatalakePublishAnnotationSet,
    );
    try {
      await preflightAnnotationPublish(
        service,
        dataSetId,
        annotationSet.annotationSetId,
        annotationSet.version,
      );
      await service.publishAnnotationSet(
        dataSetId,
        annotationSet.annotationSetId,
        { extras: withCorrelationId(scenario.queryId) },
      );
      scenario.complete('success');
      setPublishOpen(false);
      onRefresh();
    } catch (err) {
      const classification = classifyScenarioError(err);
      scenario.complete(classification.status, classification.data);
      setError(err instanceof Error ? err.message : 'Failed to publish');
    } finally {
      setIsPublishing(false);
    }
  }, [
    service,
    dataSetId,
    annotationSet.annotationSetId,
    annotationSet.version,
    canPublish,
    onRefresh,
    scenarioFactory,
  ]);

  const handleDiscardDraft = useCallback(async () => {
    setError(null);
    setIsDiscarding(true);
    const scenario = scenarioFactory.newScenario(
      ScenarioName.DatalakeDeleteAnnotationSet,
    );
    const scenarioData = { 'Scenario.DeleteMode': 'draft' };
    try {
      await service.deleteAnnotationSetDraft(
        dataSetId,
        annotationSet.annotationSetId,
        { extras: withCorrelationId(scenario.queryId) },
      );
      scenario.complete('success', scenarioData);
      setDiscardDraftOpen(false);
      onRefresh();
    } catch (err) {
      const classification = classifyScenarioError(err);
      scenario.complete(classification.status, {
        ...scenarioData,
        ...classification.data,
      });
      setError(err instanceof Error ? err.message : 'Failed to discard draft');
    } finally {
      setIsDiscarding(false);
    }
  }, [
    service,
    dataSetId,
    annotationSet.annotationSetId,
    onRefresh,
    scenarioFactory,
  ]);

  const handleDeleteAll = useCallback(async () => {
    setError(null);
    setIsDeleting(true);
    const scenario = scenarioFactory.newScenario(
      ScenarioName.DatalakeDeleteAnnotationSet,
    );
    const scenarioData = { 'Scenario.DeleteMode': 'soft' };
    try {
      await service.deleteAnnotationSet(
        dataSetId,
        annotationSet.annotationSetId,
        {
          extras: withCorrelationId(scenario.queryId),
        },
      );
      scenario.complete('success', scenarioData);
      setDeleteAllOpen(false);
      onRefresh();
    } catch (err) {
      const classification = classifyScenarioError(err);
      scenario.complete(classification.status, {
        ...scenarioData,
        ...classification.data,
      });
      setError(err instanceof Error ? err.message : 'Failed to delete');
    } finally {
      setIsDeleting(false);
    }
  }, [
    service,
    dataSetId,
    annotationSet.annotationSetId,
    onRefresh,
    scenarioFactory,
  ]);

  const handleHardDelete = useCallback(async () => {
    setError(null);
    setIsHardDeleting(true);
    const scenario = scenarioFactory.newScenario(
      ScenarioName.DatalakeDeleteAnnotationSet,
    );
    const scenarioData = { 'Scenario.DeleteMode': 'hard' };
    try {
      await service.hardDeleteAnnotationSet(
        dataSetId,
        annotationSet.annotationSetId,
        { extras: withCorrelationId(scenario.queryId) },
      );
      scenario.complete('success', scenarioData);
      setHardDeleteOpen(false);
      onRefresh();
    } catch (err) {
      const classification = classifyScenarioError(err);
      scenario.complete(classification.status, {
        ...scenarioData,
        ...classification.data,
      });
      setError(err instanceof Error ? err.message : 'Failed to hard delete');
    } finally {
      setIsHardDeleting(false);
    }
  }, [
    service,
    dataSetId,
    annotationSet.annotationSetId,
    onRefresh,
    scenarioFactory,
  ]);

  // Reset edit form when annotation set changes
  useEffect(() => {
    setEditName(annotationSet.name);
    setEditDesc(annotationSet.description ?? '');
  }, [annotationSet.name, annotationSet.description]);

  return (
    <div className={styles.expansionPanel}>
      {error !== null && <Body1 className={styles.errorState}>{error}</Body1>}

      {/* Version selector */}
      <div className={styles.actionsRow}>
        <Text size={200} className={styles.subtleLabel}>
          Viewing version:
        </Text>
        <Dropdown
          value={currentVersionLabel}
          selectedOptions={[selectedVersion]}
          onOptionSelect={(_, d) => {
            if (d.optionValue) setSelectedVersion(d.optionValue);
          }}
          className={styles.versionDropdown}
        >
          {versionOptions.map((opt) => (
            <Option key={opt.value} value={opt.value}>
              {opt.label}
            </Option>
          ))}
        </Dropdown>
      </div>

      {/* Metadata grid */}
      <div className={styles.metadataGrid}>
        <div>
          <div className={styles.metadataLabel}>SET ID</div>
          <div className={mergeClasses(styles.metadataValue, styles.monoValue)}>
            {truncateId(annotationSet.annotationSetId)}
            <Tooltip
              content={copiedSetId ? 'Copied!' : 'Copy set ID'}
              relationship="label"
            >
              <Button
                appearance="subtle"
                size="small"
                icon={<CopyIcon />}
                onClick={handleCopySetId}
                aria-label="Copy set ID"
              />
            </Tooltip>
          </div>
        </div>
        <div>
          <div className={styles.metadataLabel}>SCHEMA</div>
          <div className={styles.metadataValue}>{schemaName}</div>
        </div>
        <div>
          <div className={styles.metadataLabel}>STATUS</div>
          <div className={styles.metadataValue}>
            <Badge
              appearance="tint"
              size="small"
              color={
                annotationSet.status === 'Published'
                  ? 'success'
                  : annotationSet.status === 'Draft'
                    ? 'informative'
                    : 'warning'
              }
            >
              {annotationSet.status}
            </Badge>
          </div>
        </div>
        <div>
          <div className={styles.metadataLabel}>VERSION</div>
          <div className={styles.metadataValue}>v{annotationSet.version}</div>
        </div>
        <div>
          <div className={styles.metadataLabel}>CREATED</div>
          <div className={styles.metadataValue}>
            {formatDateTime(annotationSet.createdAt)}
          </div>
        </div>
        <div>
          <div className={styles.metadataLabel}>MODIFIED</div>
          <div className={styles.metadataValue}>
            {formatDateTime(annotationSet.updatedAt)}
          </div>
        </div>
      </div>

      {/* Action buttons — only when viewing active version */}
      {isViewingActive && (
        <div className={styles.actionsRow}>
          {isDraft && canPublish ? (
            <Button
              appearance="primary"
              size="small"
              onClick={() => setPublishOpen(true)}
            >
              Publish
            </Button>
          ) : isDraft && !canPublish ? (
            <Tooltip
              content={DATASET_ROLE_TOOLTIP_CONTRIBUTOR}
              relationship="description"
            >
              <Button appearance="primary" size="small" disabledFocusable>
                Publish
              </Button>
            </Tooltip>
          ) : null}

          {isDraft && canEdit ? (
            <Button
              appearance="outline"
              size="small"
              icon={<EditIcon />}
              onClick={() => setEditing(true)}
            >
              Edit
            </Button>
          ) : isDraft && !canEdit ? (
            <Tooltip
              content={DATASET_ROLE_TOOLTIP_ANNOTATOR}
              relationship="description"
            >
              <Button
                appearance="outline"
                size="small"
                icon={<EditIcon />}
                disabledFocusable
              >
                Edit
              </Button>
            </Tooltip>
          ) : null}

          {isDraft && canEdit ? (
            <Button
              appearance="outline"
              size="small"
              icon={<DeleteIcon />}
              onClick={() => setDiscardDraftOpen(true)}
            >
              Discard Draft
            </Button>
          ) : isDraft && !canEdit ? (
            <Tooltip
              content={DATASET_ROLE_TOOLTIP_ANNOTATOR}
              relationship="description"
            >
              <Button
                appearance="outline"
                size="small"
                icon={<DeleteIcon />}
                disabledFocusable
              >
                Discard Draft
              </Button>
            </Tooltip>
          ) : null}

          {canEdit ? (
            <Button
              appearance="outline"
              size="small"
              icon={<DeleteIcon />}
              onClick={() => setDeleteAllOpen(true)}
            >
              Delete All Versions
            </Button>
          ) : (
            <Tooltip
              content={DATASET_ROLE_TOOLTIP_ANNOTATOR}
              relationship="description"
            >
              <Button
                appearance="outline"
                size="small"
                icon={<DeleteIcon />}
                disabledFocusable
              >
                Delete All Versions
              </Button>
            </Tooltip>
          )}

          {canEdit ? (
            <Button
              appearance="outline"
              size="small"
              className={styles.dangerText}
              onClick={() => setHardDeleteOpen(true)}
            >
              Hard Delete
            </Button>
          ) : (
            <Tooltip
              content={DATASET_ROLE_TOOLTIP_ANNOTATOR}
              relationship="description"
            >
              <Button appearance="outline" size="small" disabledFocusable>
                Hard Delete
              </Button>
            </Tooltip>
          )}
        </div>
      )}

      {/* Inline edit form */}
      {editing && isViewingActive && (
        <div className={styles.dialogForm}>
          <Field label="Name" required>
            <Input value={editName} onChange={(_, d) => setEditName(d.value)} />
          </Field>
          <Field label="Description">
            <Textarea
              value={editDesc}
              onChange={(_, d) => setEditDesc(d.value)}
              rows={2}
            />
          </Field>
          <div className={styles.actionsRow}>
            <Button
              appearance="primary"
              size="small"
              onClick={() => {
                void handleSave();
              }}
              disabled={!editName.trim() || isSaving}
            >
              {isSaving ? 'Saving…' : 'Save'}
            </Button>
            <Button
              size="small"
              onClick={() => {
                setEditing(false);
                setEditName(annotationSet.name);
                setEditDesc(annotationSet.description ?? '');
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Data sub-tab content */}
      <AnnotationDataTable
        dataSetId={dataSetId}
        dataSetVersion={dataSetVersion}
        annotationSetId={annotationSet.annotationSetId}
        schemaName={schemaName}
        isDraft={isDraft}
        canEdit={isViewingActive && canEdit}
        service={service}
      />

      {/* Confirm dialogs */}
      <ConfirmActionDialog
        open={publishOpen}
        onOpenChange={setPublishOpen}
        title={`Publish "${annotationSet.name}"?`}
        description="Publishing freezes the Draft and makes it the active published version."
        confirmLabel={isPublishing ? 'Publishing…' : 'Publish'}
        isPending={isPublishing}
        confirmDisabled={!canPublish}
        onConfirm={() => {
          void handlePublish();
        }}
      />
      <ConfirmActionDialog
        open={discardDraftOpen}
        onOpenChange={setDiscardDraftOpen}
        title={`Discard the Draft of "${annotationSet.name}"?`}
        description="The Draft and any annotation data unique to it will be deleted. The previous published version (if any) becomes active again."
        confirmLabel={isDiscarding ? 'Discarding…' : 'Discard'}
        isPending={isDiscarding}
        destructive
        onConfirm={() => {
          void handleDiscardDraft();
        }}
      />
      <ConfirmActionDialog
        open={deleteAllOpen}
        onOpenChange={setDeleteAllOpen}
        title={`Delete all versions of "${annotationSet.name}"?`}
        description="Every Published version will be converted to Versioned and removed from default listings. Version history and underlying data are preserved."
        confirmLabel={isDeleting ? 'Deleting…' : 'Delete'}
        isPending={isDeleting}
        destructive
        onConfirm={() => {
          void handleDeleteAll();
        }}
      />
      <ConfirmActionDialog
        open={hardDeleteOpen}
        onOpenChange={setHardDeleteOpen}
        title="Permanently delete annotation set?"
        description="This will permanently remove the annotation set and every version, data entry, and file. This action cannot be undone."
        confirmLabel={isHardDeleting ? 'Deleting…' : 'Permanently Delete'}
        isPending={isHardDeleting}
        destructive
        onConfirm={() => {
          void handleHardDelete();
        }}
      />
    </div>
  );
}
